import { PlayerInput, PlayerSnapshot } from '../shared/types';
import { MAX_SNAPSHOTS, INTERP_BUFFER } from '../shared/constants';
import { RingBuffer } from '../shared/utils';

export interface NetworkMessage {
  type: string;
  [key: string]: unknown;
}

export interface SnapshotBuffer {
  serverTime: number;
  playerStates: Map<string, PlayerSnapshot>;
}

export class NetworkManager {
  public ws: WebSocket | null = null;
  public connected = false;
  public playerId = '';
  public ping = 0;
  public serverTimeOffset = 0;

  private url: string;
  private handlers = new Map<string, (msg: NetworkMessage) => void>();
  private sendQueue: NetworkMessage[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000;
  private pingTimer = 0;
  private lastPingTime = 0;

  constructor(url: string) {
    this.url = url;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url);
        this.ws.onopen = () => {
          this.connected = true;
          this.reconnectAttempts = 0;
          resolve();
        };
        this.ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data) as NetworkMessage;
            if (msg.type === 'pong') {
              this.ping = performance.now() - this.lastPingTime;
              return;
            }
            const handler = this.handlers.get(msg.type);
            if (handler) handler(msg);
          } catch {
            // malformed message
          }
        };
        this.ws.onclose = () => {
          this.connected = false;
          this.tryReconnect();
        };
        this.ws.onerror = () => {
          reject(new Error('WebSocket connection failed'));
        };
      } catch (e) {
        reject(e);
      }
    });
  }

  private tryReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    this.reconnectAttempts++;
    setTimeout(() => {
      this.connect().catch(() => {});
    }, this.reconnectDelay * Math.min(this.reconnectAttempts, 5));
  }

  on(type: string, handler: (msg: NetworkMessage) => void): void {
    this.handlers.set(type, handler);
  }

  off(type: string): void {
    this.handlers.delete(type);
  }

  send(msg: NetworkMessage): void {
    if (this.connected && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      this.sendQueue.push(msg);
    }
  }

  sendInput(input: PlayerInput): void {
    this.send({ type: 'input', input });
  }

  update(dt: number): void {
    this.pingTimer += dt;
    if (this.pingTimer >= 1.0) {
      this.pingTimer = 0;
      this.lastPingTime = performance.now();
      this.send({ type: 'ping', time: this.lastPingTime });
    }

    while (this.sendQueue.length > 0 && this.connected) {
      const msg = this.sendQueue.shift()!;
      this.send(msg);
    }
  }

  disconnect(): void {
    this.maxReconnectAttempts = 0;
    this.ws?.close();
    this.ws = null;
    this.connected = false;
  }

  dispose(): void {
    this.disconnect();
    this.handlers.clear();
  }
}

export class ClientPrediction {
  public inputHistory: { input: PlayerInput; state: { x: number; y: number; z: number; vx: number; vy: number; vz: number } }[] = [];
  public maxHistory = MAX_SNAPSHOTS;

  recordInput(input: PlayerInput, state: { x: number; y: number; z: number; vx: number; vy: number; vz: number }): void {
    this.inputHistory.push({ input, state: { ...state } });
    if (this.inputHistory.length > this.maxHistory) {
      this.inputHistory.shift();
    }
  }

  reconcile(
    serverState: { x: number; y: number; z: number },
    lastProcessedInput: number,
    localState: { x: number; y: number; z: number; vx: number; vy: number; vz: number }
  ): { x: number; y: number; z: number; vx: number; vy: number; vz: number } | null {
    while (this.inputHistory.length > 0 && this.inputHistory[0].input.seq <= lastProcessedInput) {
      this.inputHistory.shift();
    }

    const desync = Math.sqrt(
      (localState.x - serverState.x) ** 2 +
      (localState.y - serverState.y) ** 2 +
      (localState.z - serverState.z) ** 2
    );

    if (desync > 0.5) {
      return {
        x: serverState.x,
        y: serverState.y,
        z: serverState.z,
        vx: localState.vx,
        vy: localState.vy,
        vz: localState.vz,
      };
    }

    return null;
  }

  clear(): void {
    this.inputHistory = [];
  }
}

export class InterpolationBuffer {
  private buffer: RingBuffer<SnapshotBuffer>;
  private interpolationDelay: number;

  constructor(delay: number = INTERP_BUFFER) {
    this.buffer = new RingBuffer<SnapshotBuffer>(64);
    this.interpolationDelay = delay;
  }

  push(snapshot: SnapshotBuffer): void {
    this.buffer.push(snapshot);
  }

  getInterpolatedState(serverTime: number): { from: SnapshotBuffer; to: SnapshotBuffer; t: number } | null {
    const len = this.buffer.count();
    if (len < 2) return null;

    const targetTime = serverTime - this.interpolationDelay;
    let from: SnapshotBuffer | null = null;
    let to: SnapshotBuffer | null = null;

    for (let i = 0; i < len - 1; i++) {
      const a = this.buffer.get(i)!;
      const b = this.buffer.get(i + 1)!;
      if (a.serverTime <= targetTime && b.serverTime >= targetTime) {
        from = a;
        to = b;
        break;
      }
    }

    if (!from || !to) {
      from = this.buffer.get(len - 2)!;
      to = this.buffer.get(len - 1)!;
    }

    const range = to.serverTime - from.serverTime;
    const t = range > 0 ? (targetTime - from.serverTime) / range : 1;

    return { from, to, t: Math.max(0, Math.min(1, t)) };
  }

  clear(): void {
    this.buffer.clear();
  }
}
